import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Divider, Typography } from "@mui/material";

import ScreenContainer from "../../Components/ScreenContainer";
import WorkOrderForm from "../../PagesContent/AssetManagement/WorkOrders/WorkOrderForm";
import { CancelFormActionButton } from "../../Features/Table/GenericDraggableTable/TableStyles";
import { getWorkOrder } from "../../Api/WorkOrderApi";
import {
    prefillSerialsFromState,
    resolveEntryMode,
    resolveWorkOrderExit,
} from "../../PagesContent/AssetManagement/WorkOrders/workOrderHelpers";
import { visibleAssetManagementTabSlugs } from "./assetManagementTabs";
import { usePermissions } from "../../Context/PermissionContext";
import { usePageMeta } from "../../Hooks/usePageMeta";
import CODE from "../../Static/Constants/StatusCodes";
import { STATUS_PALETTE, STATUS_SURFACE } from "../../PagesContent/AssetManagement/statusPalette";
import { FORM_TOKENS } from "../../PagesContent/AssetManagement/WorkOrders/WorkOrderFormShared";
import { ASSET_MANAGEMENT_LABEL } from "../../Static/ModuleLabels";

// Raising a work order, on its own URL.
//
// IT IS A PAGE AND NOT A DIALOG FOR ONE REASON: bulk input. A request can carry
// hundreds of serials, and each one needs its own verdict -- a count alone does
// not say WHICH serial is the problem. That resolution table has to render
// INLINE, beside the serials that were typed, with the form still filled in,
// and a modal cannot hold it. See SerialReportTable.
//
// The table now has two ways of appearing: the "Check serials" button, which
// posts the same payload with validateOnly and gets the same body back at 200,
// and a failed submit, which gets it at 422. One endpoint, one resolver, so the
// check and the create cannot disagree.
//
// The page is a SHELL: breadcrumb, heading, context, and the form. Every piece
// of state, validation and writing lives in WorkOrderForm, which is the same
// component both routes render -- create and edit differ only by whether a
// record was fetched first.

// WHERE BACK GOES is resolved, not fixed -- see resolveWorkOrderReturn.
//
// Arriving from the Assets tab with a row selection and pressing Back used to
// land on the Work Orders queue: a different tab from the one the user was
// reading, with their selection gone. The origin travels in the same router
// state as the serials, and Back, Cancel and the breadcrumb all read it.
//
// With no origin -- the module's floating button, a bookmark, a refresh -- the
// default is still the queue, which is where a raised request appears. The
// slug is validated again on arrival by resolveRequestedTab, so a tab the org
// has hidden still lands on the first visible one rather than on nothing.

// The whole detail, because the list row carries neither movements nor items.
// Opening the form on the row alone would show an edit screen with no movements
// on it and then replace the whole set on save -- a silent wipe.
const NewWorkOrderPage = () => {
    const navigate = useNavigate();
    // LOAD-BEARING NOW, where it used to be absent.
    //
    // This page rendered ScreenContainer with pagesContent={null}, which was
    // harmless while nothing below it read page meta. The destination picker
    // now offers "+ New Customer", and the dialog behind it builds its entire
    // form from metaInfo.network.entityDefinition -- so without this the
    // option has no definition to open and is correctly hidden, which looks
    // exactly like an org that never configured one.
    const { pagesContent } = usePageMeta("AssetManagement");
    const location = useLocation();
    const { workOrderID } = useParams();
    const isEdit = Boolean(workOrderID);

    // The Assets tab's selection, handed over in ROUTER STATE and not in the
    // query string: a selection can be two hundred serials, which is a URL no
    // browser, proxy or log line should be asked to carry, and which would be
    // bookmarked and shared as though it meant something.
    //
    // Absent on a direct visit, on a refresh and from the module's floating
    // button. All three are a blank form -- prefillSerialsFromState returns a
    // list in every branch, including that one.
    const initialSerials = useMemo(
        () => prefillSerialsFromState(location.state),
        [location.state]
    );

    // Role-level only, and deliberately: this route has no pageMeta of its own,
    // and fetching it purely to decide where a button points would put a
    // request on the critical path of raising a work order.
    const { hasPermission } = usePermissions();
    // BACK MEANS BACK. History first where there is history -- after the
    // floating button's chooser or a row selection, the previous entry is the
    // tab the user was actually on -- then the origin carried in router state,
    // then the queue. See resolveWorkOrderExit.
    const returnTo = useMemo(
        () =>
            resolveWorkOrderExit({
                state: location.state,
                allowedSlugs: visibleAssetManagementTabSlugs(hasPermission),
                historyLength: window.history.length,
            }),
        [location.state, hasPermission]
    );

    // A SAVE NEVER REPLAYS HISTORY: the entry the user would go back to is the
    // form they just submitted. Same origin resolution, history refused, so
    // the two destinations cannot disagree about where the user came from.
    const savedTo = useMemo(
        () =>
            resolveWorkOrderExit({
                state: location.state,
                allowedSlugs: visibleAssetManagementTabSlugs(hasPermission),
                afterSave: true,
            }),
        [location.state, hasPermission]
    );

    // ONE ASSET OR SEVERAL, answered before this page opened: by the floating
    // button's two options, or by how many rows were ticked on the Assets tab
    // -- a selection has already answered it, so the chooser is skipped. A
    // missing state falls back to single, the common case.
    const entryMode = useMemo(
        () => resolveEntryMode(location.state),
        [location.state]
    );

    // WHICH MOVEMENT, asked by the FAB's tree before this page opened. Absent
    // on every other way in -- a deep link, a refresh, an edit -- and the
    // form's dropdown then opens unchosen, exactly as it always did.
    const initialMovementTypeCode =
        (location.state && typeof location.state === "object"
            ? location.state.movementTypeCode
            : "") || "";

    const [workOrder, setWorkOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(Boolean(workOrderID));
    const [errorMessage, setErrorMessage] = useState("");
    // Reported up by the form. Every way OUT of this page is locked while the
    // write is in flight: the request has already gone, and leaving would hide
    // the per-serial report that is the only diagnosis of a failure.
    const [saving, setSaving] = useState(false);
    // WHICH KIND of work order, reported up by the form -- it owns the config
    // the type is resolved from. Empty until that config lands, which is what
    // leaves the heading unsuffixed for a moment rather than suffixed with an
    // empty string and a dangling dash.
    const [workOrderTypeLabel, setWorkOrderTypeLabel] = useState("");

    useEffect(() => {
        if (!workOrderID) {
            setWorkOrder(null);
            setIsLoading(false);
            setErrorMessage("");
            return undefined;
        }

        let cancelled = false;
        setIsLoading(true);
        setErrorMessage("");
        (async () => {
            const res = await getWorkOrder(workOrderID);
            if (cancelled) return;
            if (res?.responseStatus?.code === CODE.SUCCESS && res?.data) {
                setWorkOrder(res.data);
            } else {
                setWorkOrder(null);
                setErrorMessage(
                    res?.responseStatus?.msg || "Could not open this work order"
                );
            }
            setIsLoading(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [workOrderID]);

    // Back to wherever the user came from, which refetches on mount -- so a
    // request just raised is in the list the moment they land on it.
    const goToQueue = useCallback(
        () => navigate(savedTo.path),
        [navigate, savedTo.path]
    );

    // The USER's way out. Uses history where there is any, so they land on the
    // screen they left rather than on a tab they may never have opened -- and
    // whatever that screen held is still there.
    const leave = useCallback(() => {
        if (returnTo.useHistory) {
            navigate(-1);
            return;
        }
        navigate(returnTo.path);
    }, [navigate, returnTo]);

    // The USER's way out, which is refused mid-write. Kept separate from
    // goToQueue because a successful save navigates from inside the request --
    // `saving` is still true at that point, and routing the success through the
    // guard would leave the maker stranded on a form that had already been
    // accepted.
    const goBack = useCallback(() => {
        if (saving) return;
        leave();
    }, [leave, saving]);

    // NAMED BY TYPE. "New Work Order" alone does not distinguish this page
    // from the one a second work order type would open, and the maker is about
    // to raise something that lands in a queue beside other types. The edit
    // heading is not suffixed: the reference id already identifies the record,
    // and the type is on the row the user clicked to get here.
    const heading = isEdit
        ? `Edit ${workOrder?.workOrderReferenceID || "Work Order"}`
        : workOrderTypeLabel
            ? `New Work Order - ${workOrderTypeLabel}`
            : "New Work Order";

    return (
        <ScreenContainer
            pagesContent={{ ...pagesContent, metaInfo: pagesContent?.pageMeta }}
        >
            {/* ONE container owns the horizontal inset, per the page layout
                rule. Nothing below re-declares px. */}
            <Box sx={{ px: 3, pt: 2, pb: 3 }}>
                <Box
                    data-testid="work-order-page-breadcrumb"
                    sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                    <Typography
                        component="button"
                        type="button"
                        data-testid="work-order-page-back"
                        onClick={goBack}
                        disabled={saving}
                        sx={{
                            border: "none",
                            background: "none",
                            padding: 0,
                            cursor: saving ? "default" : "pointer",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: saving ? FORM_TOKENS.disabled : FORM_TOKENS.purple,
                            fontFamily: "inherit",
                        }}
                    >
                        {pagesContent?.pageHeader || ASSET_MANAGEMENT_LABEL}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: FORM_TOKENS.inkFaint }}>/</Typography>
                    {/* The tab this form will return to, named as the org
                        names it. It followed the destination the moment Back
                        stopped always meaning the queue. */}
                    <Typography
                        data-testid="work-order-page-breadcrumb-origin"
                        sx={{ fontSize: "13px", color: FORM_TOKENS.inkFaint }}
                    >
                        {returnTo.label}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: FORM_TOKENS.inkFaint }}>/</Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: FORM_TOKENS.inkBody }}>
                        {isEdit ? "Edit" : "New"}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="fw700s20lh24c2E2E2E">{heading}</Typography>
                        {/* The maker/checker split, said before the form rather
                            than discovered when the Review button is missing:
                            this request is raised by you and signed off by
                            somebody else. The one exception -- a type with
                            requiresApproval = 0 -- is called out beside the
                            type picker, where the choice is actually made. */}
                        <Typography sx={{ fontSize: "12px", color: FORM_TOKENS.inkFaint, mt: 0.5 }}>
                            You are raising this request. Someone else reviews it before
                            anything moves.
                        </Typography>
                        {/* Said once, here, rather than left for the user to
                            infer from a box that was not empty when they
                            arrived. */}
                        {!isEdit && initialSerials.length > 0 && (
                            <Typography
                                data-testid="work-order-page-prefill-note"
                                sx={{ fontSize: "12px", color: FORM_TOKENS.purpleDark, mt: 0.5 }}
                            >
                                {initialSerials.length} serial
                                {initialSerials.length === 1 ? "" : "s"} carried over from
                                the assets you selected.
                            </Typography>
                        )}
                    </Box>
                    <CancelFormActionButton
                        data-testid="work-order-page-back-button"
                        onClick={goBack}
                        disabled={saving}
                        sx={{ flexShrink: 0 }}
                    >
                        Back to {returnTo.label}
                    </CancelFormActionButton>
                </Box>

                <Divider sx={{ mt: 2, mb: 3, borderColor: FORM_TOKENS.fieldBorder }} />

                {isLoading ? (
                    <Box
                        data-testid="work-order-page-loading"
                        sx={{ display: "flex", justifyContent: "center", py: 8 }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                ) : errorMessage ? (
                    // A failed detail fetch is NOT an empty create form. Falling
                    // through to one would offer a blank New Work Order under an
                    // "Edit WO-123" heading, and saving it would raise a second
                    // request rather than change the first.
                    <Box
                        data-testid="work-order-page-error"
                        sx={{
                            border: `1px solid ${STATUS_PALETTE.error.border}`,
                            backgroundColor: STATUS_SURFACE.error,
                            borderRadius: "8px",
                            p: 3,
                        }}
                    >
                        <Typography sx={{ fontSize: "14px", color: FORM_TOKENS.danger, mb: 1 }}>
                            {errorMessage}
                        </Typography>
                        <CancelFormActionButton
                            data-testid="work-order-page-error-back"
                            onClick={goBack}
                        >
                            Back to {returnTo.label}
                        </CancelFormActionButton>
                    </Box>
                ) : (
                    <WorkOrderForm
                        workOrder={workOrder}
                        initialSerials={initialSerials}
                        backLabel={returnTo.label}
                        entryMode={entryMode}
                        initialMovementTypeCode={initialMovementTypeCode}
                        metaInfo={pagesContent?.pageMeta}
                        onCancel={goBack}
                        onSaved={goToQueue}
                        onSavingChange={setSaving}
                        onWorkOrderTypeLabelChange={setWorkOrderTypeLabel}
                    />
                )}
            </Box>
        </ScreenContainer>
    );
};

export default NewWorkOrderPage;
